<template>
  <div>
    <ExpandableItemCard
      :title="constituent?.name || 'New Constituent'"
      :subtitle="
        constituent?.amount
          ? `(${constituent?.amount}${constituent?.unit ? ' ' + constituent?.unit : ''})`
          : ''
      "
      item-label="constituent"
      :index="index"
      :total-items="totalItems"
      :start-expanded="startExpanded"
      :expanded="expanded"
      :active="active"
      :has-warning="!!missingNameWarning"
      margin-bottom="mb-2"
      nested
      @toggle="$emit('toggle')"
    >
      <template #header-title>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="formattedConstituentName"></span>
      </template>
      <template #header-actions>
        <button
          type="button"
          class="delete-header-btn"
          aria-label="Remove this constituent"
          @click.stop="$emit('removed')"
        >
          <i class="fas fa-trash" aria-hidden="true"></i>
        </button>
      </template>
      <template #header-subtitle>
        <AmountWithUnitInput
          :amount="constituent?.amount"
          :unit="constituent?.unit"
          :uid="constituent?._uid"
          :allowed-units="CONSTITUENT_UNITS"
          class="is-small header-amount-input"
          placeholder="Amt"
          @update:amount="updateField('amount', $event, false)"
          @update:unit="updateField('unit', $event)"
          @blur="emitSave"
          @click.stop
        />
      </template>

      <div class="field mb-2">
        <div class="is-flex is-align-items-start">
          <label
            :for="`constituent-name-${constituent._uid}`"
            class="label is-small mb-0 mr-2 label-inline"
          >
            Ingredient
            <IconHint :content="HINT_TEXTS.CUSTOM_FORMATTING" />
          </label>
          <div class="control is-flex-grow-1">
            <input
              :id="`constituent-name-${constituent._uid}`"
              type="text"
              class="input is-small"
              :value="constituent.name"
              name="ingredient"
              placeholder="e.g., Green Tea Extract"
              @input="updateField('name', $event.target.value, false)"
              @blur="emitSave"
            />
          </div>
        </div>
        <div class="checkbox-group mt-1 content-indent">
          <label v-if="hasParens" class="checkbox is-size-7 is-flex is-align-items-center">
            <input
              type="checkbox"
              class="mr-1"
              :checked="constituent.italicize_parenthetical"
              data-test-id="constituent-italicize-checkbox"
              @change="updateField('italicize_parenthetical', $event.target.checked)"
            />
            <span>{{ italicizeLabel }}</span>
          </label>
          <ExtraTextField
            v-if="displayMode !== 'inline'"
            :model-value="constituent.extra_text"
            :is-expanded="hasExtraText || showExtraTextInput"
            checkbox-test-id="constituent-extra-text-checkbox"
            input-test-id="constituent-extra-text-input"
            @toggle="toggleExtraText"
            @update:model-value="updateExtraText"
          />
          <label
            v-if="displayMode !== 'inline'"
            class="checkbox is-size-7 is-flex is-align-items-center"
          >
            <input
              type="checkbox"
              class="mr-1"
              :checked="hasFootnote"
              data-test-id="constituent-footnote-checkbox"
              @change="toggleFootnote($event.target.checked)"
            />
            <span>Footnote</span>
          </label>
        </div>
        <FootnoteItemEditor
          v-if="displayMode !== 'inline'"
          :item="constituent"
          placeholder="e.g., Standardized to 50% polyphenols"
          on-amount-label="Show on amount"
          class="content-indent"
          bordered
          @update="handleFootnoteUpdate"
        />
      </div>

      <div class="field mb-2">
        <div class="is-flex is-align-items-start">
          <label
            :for="`constituent-source-${constituent._uid}`"
            class="label is-small mb-0 mr-2 label-inline pt-1"
          >
            Source
            <IconHint :content="HINT_TEXTS.CUSTOM_FORMATTING" />
          </label>
          <div class="control is-flex-grow-1">
            <input
              :id="`constituent-source-${constituent._uid}`"
              type="text"
              class="input is-small"
              :value="constituent.source"
              name="source"
              placeholder="e.g., Plant based"
              data-test-id="constituent-source-input"
              @input="updateField('source', $event.target.value, false)"
              @blur="emitSave"
            />
          </div>
        </div>
      </div>

      <div class="field mb-2">
        <div class="is-flex is-align-items-start">
          <label
            :for="`constituent-amount-${constituent._uid}`"
            class="label is-small mb-0 mr-2 label-inline pt-1"
            >Amount</label
          >
          <div class="control is-expanded">
            <AmountWithUnitInput
              :input-id="`constituent-amount-${constituent._uid}`"
              :amount="constituent.amount"
              :unit="constituent.unit"
              :uid="constituent._uid"
              :allowed-units="CONSTITUENT_UNITS"
              @update:amount="updateField('amount', $event, false)"
              @update:unit="updateField('unit', $event)"
              @blur="emitSave"
            />
          </div>
        </div>
      </div>

      <div v-if="displayMode !== 'inline'" class="checkbox-group content-indent mb-2">
        <label class="checkbox is-size-7 is-flex is-align-items-center">
          <input
            type="checkbox"
            class="mr-1"
            :checked="constituent.hide_dagger"
            data-test-id="constituent-hide-dagger-checkbox"
            @change="updateField('hide_dagger', $event.target.checked)"
          />
          <span>{{ hideDaggerLabel }}</span>
        </label>
      </div>

      <div v-if="missingNameWarning">
        <p class="notification is-warning is-light bordered-left py-2 px-3 mt-2">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          {{ missingNameWarning }}
        </p>
      </div>
    </ExpandableItemCard>
  </div>
</template>

<script setup>
  import { computed, inject } from "vue";
  import ExpandableItemCard from "./expandable-item-card.vue";
  import AmountWithUnitInput from "./amount-with-unit-input.vue";
  import FootnoteItemEditor from "./footnote-item-editor.vue";
  import { formatLabelName } from "./utils/formatting";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import { useFootnoteExtraText } from "./composables/use-footnote-extra-text";
  import { useFormattingLabels } from "./composables/use-formatting-labels";
  import { CONSTITUENT_UNITS, HINT_TEXTS } from "./utils/constants";
  import IconHint from "../icon-hint.vue";
  import ExtraTextField from "./extra-text-field.vue";

  const props = defineProps({
    constituent: { type: Object, required: true },
    index: { type: Number, required: true },
    totalItems: { type: Number, required: true },
    blendIndex: { type: Number, default: -1 },
    parentDisplayMode: { type: String, default: null },
    startExpanded: { type: Boolean, default: false },
    expanded: { type: Boolean, default: undefined },
    active: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
  });

  const emit = defineEmits(["update", "removed", "save", "toggle"]);

  const store = useSupplementLabelStore();
  const triggerSave = inject("triggerSave");

  const parentBlend = computed(() =>
    props.blendIndex >= 0 ? store.blends[props.blendIndex] : null
  );
  const displayMode = computed(
    () => parentBlend.value?.display_mode || props.parentDisplayMode || "inline"
  );

  const constituentRef = computed(() => props.constituent);
  const composableUpdate = (updates) => emit("update", { updates });
  const {
    hasFootnote,
    hasExtraText,
    showExtraTextInput,
    toggleFootnote,
    toggleExtraText,
    updateExtraText,
  } = useFootnoteExtraText(constituentRef, composableUpdate, { onSave: triggerSave });

  const { hasParens, italicizeLabel, hideDaggerLabel } = useFormattingLabels(
    constituentRef,
    () => props.constituent.name
  );

  const missingNameWarning = computed(() => {
    if (props.editing) return null;
    if (props.constituent?.name) return null;
    const hasContent = props.constituent?.amount || props.constituent?.source;
    if (!hasContent) return null;
    return "A constituent name is required.";
  });

  const formattedConstituentName = computed(() => {
    const name = props.constituent?.name || "New Constituent";
    return formatLabelName(name, {
      italicizeParenthetical: props.constituent?.italicize_parenthetical,
      disableSubscripts: true,
    });
  });

  function updateField(field, value, shouldEmit = true) {
    emit("update", { field, value, shouldEmit });
  }

  function emitSave() {
    emit("save");
  }

  function handleFootnoteUpdate(updates) {
    emit("update", { updates });
  }
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_mixins";
  @import "~/label/css/labels/supplements/supplement_editor";
</style>
